import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import { isString, __DEV__ } from '../../utils/assertion.esm.js';
import '../../utils/ssr.esm.js';
import '../../utils/number.esm.js';
import '../../utils/array.esm.js';
import '../../utils/breakpoint.esm.js';
import '../../utils/context.esm.js';
import '../../utils/dom.esm.js';
import '../../utils/tabbable.esm.js';
import '../../utils/dom-query.esm.js';
import '../../utils/logger.esm.js';
import '../../utils/focus.esm.js';
import '../../utils/function.esm.js';
import '../../utils/keydown.esm.js';
import '../../utils/lazy.esm.js';
import { omit, pick } from '../../utils/object.esm.js';
import '../../utils/react-helpers.esm.js';
import '../../utils/style-props.esm.js';
import '../../utils/tailwind.esm.js';
import Box from '../Box/index.esm.js';
import Button from '../Button/index.esm.js';
import IconButton from '../IconButton/index.esm.js';
import { useSafeLayoutEffect } from '../../hooks/use-safe-layout-effects.esm.js';
import '../../hooks/use-callback-ref.esm.js';
import '../../hooks/use-clickable.esm.js';
import '../../hooks/use-clipboard.esm.js';
import '../../hooks/use-combobox-dropdown-style.esm.js';
import '../../hooks/use-combobox-style.esm.js';
import '../../hooks/use-controllable.esm.js';
import '../../hooks/use-local-storage.esm.js';
import '../../hooks/use-media.esm.js';
import '../../hooks/use-prefers-dark-mode.esm.js';
import '../../hooks/use-dark-mode.esm.js';
import '../../hooks/use-force-update.esm.js';
import '../../hooks/use-descendant.esm.js';
import '../../hooks/use-id.esm.js';
import '../../hooks/use-previous.esm.js';
import '../../hooks/use-disclosure.esm.js';
import '../../hooks/use-dropdown-style.esm.js';
import '../../hooks/use-latest-ref.esm.js';
import '../../hooks/use-event-listener.esm.js';
import '../../hooks/use-event-listener-v2.esm.js';
import '../../hooks/use-update-effect.esm.js';
import '../../hooks/use-focus-on-hide.esm.js';
import '../../hooks/use-pointer-event.esm.js';
import '../../hooks/use-focus-on-pointerdown.esm.js';
import '../../hooks/use-focus-on-show.esm.js';
import '../../hooks/use-fork-ref.esm.js';
import '../../hooks/use-hints.esm.js';
import '../../hooks/use-input-style.esm.js';
import '../../hooks/use-key-press.esm.js';
import '../../hooks/use-lock-body-scroll.esm.js';
import '../../hooks/use-platform.esm.js';
import '../../hooks/use-shortcut.esm.js';
import Portal from '../Portal/index.esm.js';
import VisuallyHidden from '../VisuallyHidden/index.esm.js';
import { useTooltip } from './use-tooltip.esm.js';
export { useTooltip } from './use-tooltip.esm.js';

/**
 * Tooltips display informative text when users hover, focus on, or tap an element.
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#tooltip
 */

var Tooltip = /*#__PURE__*/React__default.forwardRef(function (props, ref) {
  var _child$props;

  var children = props.children,
      label = props.label,
      shouldWrapChildren = props.shouldWrapChildren,
      ariaLabel = props['aria-label'],
      _props$arrowSize = props.arrowSize,
      arrowSize = _props$arrowSize === void 0 ? 8 : _props$arrowSize,
      _props$placement = props.placement,
      placement = _props$placement === void 0 ? 'top' : _props$placement,
      _props$hoverable = props.hoverable,
      hoverable = _props$hoverable === void 0 ? false : _props$hoverable,
      getPortalContainer = props.getPortalContainer,
      rest = _objectWithoutPropertiesLoose(props, ["children", "label", "shouldWrapChildren", "aria-label", "arrowSize", "placement", "hoverable", "getPortalContainer"]);

  var _useTooltip = useTooltip(_extends({
    arrowSize: arrowSize,
    placement: placement,
    hoverable: hoverable
  }, props)),
      open = _useTooltip.open,
      getTriggerProps = _useTooltip.getTriggerProps,
      getTooltipProps = _useTooltip.getTooltipProps,
      getArrowProps = _useTooltip.getArrowProps,
      update = _useTooltip.update,
      hide = _useTooltip.hide;

  useSafeLayoutEffect(function () {
    if (label) {
      update();
    } else {
      hide();
    }
  }, [label, children]);
  var shouldWrap = isString(children) || shouldWrapChildren; // Ensure tooltip has only one child node if `shouldWrap` is false, otherwise default to `children`

  var child = !shouldWrap && React__default.Children.only(children) || children;
  var trigger; // If the element is disabled and it's a button, disable pointer events, also automatically wrap

  if ((_child$props = child.props) == null ? void 0 : _child$props.disabled) {
    if (child.type === 'button') {
      var _child$props$classNam, _child$props2;

      shouldWrap = true;
      child = React__default.cloneElement(child, _extends({}, child.props, {
        className: "pointer-events-none " + ((_child$props$classNam = (_child$props2 = child.props) == null ? void 0 : _child$props2.className) != null ? _child$props$classNam : '')
      }));
    } else if (child.type === Button || child.type === IconButton) {
      shouldWrap = true;
      child = React__default.cloneElement(child, _extends({}, child.props, {
        pointerEvents: 'pointer-events-none'
      }));
    }
  }

  if (shouldWrap) {
    trigger = // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    React__default.createElement("span", _extends({
      className: "inline-block",
      tabIndex: 0
    }, getTriggerProps()), child);
  } else {
    trigger = React__default.cloneElement(child, getTriggerProps(child.props));
  }

  var hasAriaLabel = !!ariaLabel;
  var internalTooltipProps = getTooltipProps(_extends({}, rest, {
    ref: ref
  }));
  var tooltipProps = hasAriaLabel ? omit(internalTooltipProps, ['role', 'id']) : internalTooltipProps;
  var hiddenProps = pick(internalTooltipProps, ['role', 'id']); // If the `label` or `aria-label` is empty, there's no
  // point showing the tooltip. Let's simply return back the children

  if (!(label || ariaLabel)) {
    return React__default.createElement(React__default.Fragment, null, child);
  }

  return React__default.createElement(React__default.Fragment, null, trigger, open && React__default.createElement(Portal, {
    getContainer: getPortalContainer
  }, React__default.createElement(Box, _extends({}, tooltipProps), label, hasAriaLabel && React__default.createElement(VisuallyHidden, _extends({}, hiddenProps), ariaLabel), React__default.createElement(Box, _extends({}, getArrowProps())))));
});

if (__DEV__) {
  Tooltip.displayName = 'Tooltip';
}

export default Tooltip;
//# sourceMappingURL=index.esm.js.map
